/**
 * Messenger WebSocket Service
 *
 * Handles real-time messaging communication including:
 * - New messages
 * - Message updates and deletions
 * - Typing indicators
 * - Presence updates
 * - Automatic reconnection
 */

import { eventBus, Events } from './events';
import { mapMessage, mapConversation } from './messenger-api';
import type {
  MessengerWsMessage,
  Message,
  TypingIndicator,
  PresenceUpdate,
  Conversation,
} from '../types/messenger';

class MessengerWebSocketService {
  private ws: WebSocket | null = null;
  private url = '';
  private token = '';
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 10;
  private reconnectInterval = 1000;
  private pingInterval: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private isManualClose = false;

  /**
   * Configure the WebSocket connection
   */
  configure(url: string, token: string) {
    this.url = url;
    this.token = token;
  }

  /**
   * Update the authentication token
   */
  setToken(token: string) {
    this.token = token;
    // Reconnect with new token if currently connected
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.disconnect();
      this.connect();
    }
  }

  /**
   * Connect to the messenger WebSocket
   */
  connect() {
    if (!this.url || !this.token) {
      console.warn('[MessengerWS] Missing URL or token, cannot connect');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return; // Already connected
    }

    this.isManualClose = false;

    try {
      const wsUrl = new URL(this.url);
      wsUrl.searchParams.set('token', this.token);
      this.ws = new WebSocket(wsUrl.toString());

      this.ws.onopen = this.handleOpen.bind(this);
      this.ws.onmessage = this.handleMessage.bind(this);
      this.ws.onclose = this.handleClose.bind(this);
      this.ws.onerror = this.handleError.bind(this);
    } catch (e) {
      console.error('[MessengerWS] Failed to create WebSocket:', e);
      this.scheduleReconnect();
    }
  }

  /**
   * Disconnect from the WebSocket
   */
  disconnect() {
    this.isManualClose = true;
    this.stopPing();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
  }

  /**
   * Send a message through the WebSocket
   */
  send(type: string, payload: unknown) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      console.warn('[MessengerWS] Cannot send, WebSocket not connected');
      return false;
    }

    try {
      this.ws.send(
        JSON.stringify({
          type,
          payload,
          timestamp: Date.now(),
        })
      );
      return true;
    } catch (e) {
      console.error('[MessengerWS] Failed to send message:', e);
      return false;
    }
  }

  /**
   * Send typing indicator
   */
  sendTypingStart(conversationId: string) {
    return this.send('typing.start', { conversationId });
  }

  /**
   * Stop typing indicator
   */
  sendTypingStop(conversationId: string) {
    return this.send('typing.stop', { conversationId });
  }

  /**
   * Mark conversation as read
   */
  sendMarkRead(conversationId: string, messageId: string) {
    return this.send('conversation.read', { conversationId, messageId });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  private handleOpen() {
    console.log('[MessengerWS] Connected');
    this.reconnectAttempts = 0;
    this.startPing();
    eventBus.emit(Events.WS_CONNECTED, { service: 'messenger' });
  }

  private handleMessage(event: MessageEvent) {
    try {
      const message: MessengerWsMessage = JSON.parse(event.data);
      this.routeMessage(message);
    } catch (e) {
      console.error('[MessengerWS] Failed to parse message:', e);
    }
  }

  private routeMessage(message: MessengerWsMessage) {
    switch (message.type) {
      case 'message.new':
        this.handleNewMessage(message.payload as Message);
        break;
      case 'message.updated':
        this.handleMessageUpdated(message.payload as Message);
        break;
      case 'message.deleted':
        this.handleMessageDeleted(message.payload as { conversationId: string; messageId: string });
        break;
      case 'typing.start':
        this.handleTypingStart(message.payload as TypingIndicator);
        break;
      case 'typing.stop':
        this.handleTypingStop(message.payload as { conversationId: string; userId: string });
        break;
      case 'presence.changed':
        this.handlePresenceChanged(message.payload as PresenceUpdate);
        break;
      case 'conversation.updated':
        this.handleConversationUpdated(message.payload as Conversation);
        break;
      case 'pong':
      case 'connected':
        // Heartbeat response / connection acknowledgement, no action needed
        break;
      case 'error':
        this.handleWsError(message.payload as { code: string; message: string });
        break;
      default:
        console.warn('[MessengerWS] Unknown message type:', message.type);
    }
  }

  private handleNewMessage(message: Message) {
    // Backend sends snake_case — map to camelCase frontend type
    const mapped = (message as any).conversation_id ? mapMessage(message as any) : message;
    eventBus.emit(Events.MESSENGER_MESSAGE_RECEIVED, mapped);
    // Also emit unread changed since we received a new message
    eventBus.emit(Events.MESSENGER_UNREAD_CHANGED, {
      conversationId: mapped.conversationId,
      increment: 1,
    });
  }

  private handleMessageUpdated(message: Message) {
    const mapped = (message as any).conversation_id ? mapMessage(message as any) : message;
    eventBus.emit('messenger:message:updated', mapped);
  }

  private handleMessageDeleted(data: { conversationId: string; messageId: string }) {
    eventBus.emit('messenger:message:deleted', data);
  }

  private handleTypingStart(indicator: TypingIndicator) {
    eventBus.emit(Events.MESSENGER_TYPING, {
      ...indicator,
      isTyping: true,
    });
  }

  private handleTypingStop(data: { conversationId: string; userId: string }) {
    eventBus.emit(Events.MESSENGER_TYPING, {
      ...data,
      isTyping: false,
    });
  }

  private handlePresenceChanged(presence: PresenceUpdate) {
    eventBus.emit(Events.MESSENGER_PRESENCE, presence);
  }

  private handleConversationUpdated(conversation: Conversation) {
    // Backend sends snake_case — map to camelCase frontend type
    const mapped = (conversation as any).is_private !== undefined ? mapConversation(conversation as any) : conversation;
    eventBus.emit('messenger:conversation:updated', mapped);
    // Update unread count
    eventBus.emit(Events.MESSENGER_UNREAD_CHANGED, {
      conversationId: mapped.id,
      unreadCount: mapped.unreadCount,
    });
  }

  private handleWsError(error: { code: string; message: string }) {
    console.error('[MessengerWS] Server error:', error);
    eventBus.emit('messenger:error', error);
  }

  private handleClose(event: CloseEvent) {
    console.log('[MessengerWS] Disconnected:', event.code, event.reason);
    this.stopPing();
    eventBus.emit(Events.WS_DISCONNECTED, { service: 'messenger', code: event.code });

    // Attempt reconnection unless manually closed
    if (!this.isManualClose) {
      this.scheduleReconnect();
    }
  }

  private handleError(event: Event) {
    console.error('[MessengerWS] WebSocket error:', event);
  }

  private scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('[MessengerWS] Max reconnect attempts reached');
      return;
    }

    this.clearReconnectTimer();

    // Exponential backoff
    const delay = this.reconnectInterval * Math.pow(2, this.reconnectAttempts);
    console.log(`[MessengerWS] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      this.connect();
    }, delay);
  }

  private clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private startPing() {
    this.stopPing();
    // Send ping every 30 seconds
    this.pingInterval = setInterval(() => {
      this.send('ping', {});
    }, 30000);
  }

  private stopPing() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }
}

// Export singleton instance
export const messengerWsService = new MessengerWebSocketService();

export default messengerWsService;
